import { useSignalRNotifications } from './useSignalRNotifications';

/**
 * DTO for timeline/history notifications via SignalR
 */
export interface TimelineNotificationDto {
  /** Updated unread count for the timeline */
  unreadCount: number;
}

/**
 * Configuration for timeline notifications
 */
export const TIMELINE_NOTIFICATIONS_CONFIG = {
  route: '/signalr/flux-activite',
  joinMethod: 'JoinRiskGroup',
  leaveMethod: 'LeaveRiskGroup',
  eventName: 'HistoryUpdate'
} as const;

/**
 * Hook for timeline/history notifications via SignalR
 *
 * @param riskId - ID of the risk to join the notification group
 * @param onUnreadCountUpdate - Optional callback when unread count is updated
 *
 * @example
 * ```tsx
 * const { isConnected, latestNotification, memoizedConnect, memoizedDisconnect } =
 *   useTimelineNotifications(riskId, (notification) => {
 *     // Update unread count in state
 *     dispatch({ type: 'UPDATE_UNREAD_COUNT', payload: notification.unreadCount });
 *   });
 *
 * useEffect(() => {
 *   memoizedConnect();
 *   return memoizedDisconnect;
 * }, [memoizedConnect, memoizedDisconnect]);
 * ```
 */
export const useTimelineNotifications = (
  riskId?: string,
  onUnreadCountUpdate?: (notification: TimelineNotificationDto) => void
) => {
  const {
    connection,
    isConnected,
    error,
    memoizedConnect,
    memoizedDisconnect,
    notifications,
    latestNotification,
    clearNotifications
  } = useSignalRNotifications<TimelineNotificationDto>({
    ...TIMELINE_NOTIFICATIONS_CONFIG,
    groupId: riskId,
    onNotification: onUnreadCountUpdate
  });

  return {
    // Connection state
    connection,
    isConnected,
    error,

    // Connection methods
    memoizedConnect,
    memoizedDisconnect,

    // Notification data
    notifications,
    latestNotification,

    // Utility methods
    clearNotifications
  };
};
